import classnames from "classnames"
import { Icon } from "../Icon"
import { ElementType, forwardRef, ReactNode } from "react"
import {
  COLOUR_SCHEMES,
  BASE_CLASSES,
  COLOUR_SCHEMES_HOVER,
  BASE_CLASSES_DISABLED,
  COLOUR_SCHEMES_DISABLED,
  BASE_CLASSES_ACTIVE,
  BASE_CLASSES_FOCUS,
} from "../Button/constants"
import { BASE_CLASSES_MD, BASE_CLASSES_SM } from "./constants"
import type { IconButtonProps, IconButtonPropsWithRef } from "./types"
import { FOCUS_STATE_BASE_CLASSES } from "../utils/focusStateClasses"
import { Box, PolymorphicRef } from "../Box"
import { LoadingSpinner } from "../LoadingSpinner"
import { getButtonBoxAttrs } from "../Button"

const defaultElement = "button"

export const IconButton: <E extends ElementType = typeof defaultElement>(
  props: IconButtonPropsWithRef<E>,
) => ReactNode | null = forwardRef(
  <E extends ElementType = typeof defaultElement>(
    props: Omit<IconButtonProps<E>, "ref">,
    ref: PolymorphicRef,
  ) => {
    const {
      href: hrefProp,
      as: element,
      className,
      disabled,
      variation = "primary",
      iconName,
      size = "md",
      isLoading,
      onClick: onClickProp,
      ...rest
    } = props
    const { isDisabledStyling, ...attrs } = getButtonBoxAttrs({
      as: element,
      disabled,
      href: hrefProp ?? undefined,
      isLoading,
      onClick: onClickProp,
      ...rest,
    })

    const isSM = size === "sm"
    const isMD = size === "md"

    const _className = classnames(
      COLOUR_SCHEMES[variation],
      BASE_CLASSES,
      FOCUS_STATE_BASE_CLASSES,
      "aspect-square", // guarantees that the button always stays square
      {
        [BASE_CLASSES_MD]: isMD,
        [BASE_CLASSES_SM]: isSM,
        [BASE_CLASSES_DISABLED]: isDisabledStyling,
        [BASE_CLASSES_ACTIVE]: !isDisabledStyling,
        [BASE_CLASSES_FOCUS]: !isDisabledStyling,
        [COLOUR_SCHEMES_HOVER[variation] as string]: !isDisabledStyling,
        [COLOUR_SCHEMES_DISABLED[variation] as string]: isDisabledStyling,
      },
      className,
    )

    return (
      <Box className={_className} ref={ref} {...attrs}>
        <div className="flex h-full items-center justify-center">
          {!isLoading && <Icon name={iconName} size={size} />}
          {isLoading && <LoadingSpinner size={size} title="Loading" />}
        </div>
      </Box>
    )
  },
)
